import { useEffect, useState } from 'react';
import { actionType, POSTAL_CODE_REGEX } from 'config/constants';
import { CircularProgress, FormControlLabel, Grid, Typography } from '@material-ui/core';
import { isNil } from 'lodash';
import { useFormik } from 'formik';
import { useTheme } from '@material-ui/core/styles';
import * as Yup from 'yup';
import ActionOnAddressModal from 'components/ActionOnAddressModal';
import AddressFields from 'components/AddressFields';
import CheckIcon from '@material-ui/icons/Check';
import DefaultButton from 'components/DefaultButton';
import DefaultSwitch from 'components/DefaultSwitch';
import SwitchStatus from 'components/SwitchStatus';
import useShowActionModal from 'hooks/useShowActionModal';
import useStyles from './styles';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';

const validationSchema = Yup.object({
  address_name: Yup.string().required('Champ obligatoire'),
  city_name: Yup.string().required('Champ obligatoire'),
  street_index: Yup.string(),
  street_number: Yup.number().required('Champ obligatoire'),
  title: Yup.string().required('Champ obligatoire'),
  zip_code: Yup.string()
    .matches(POSTAL_CODE_REGEX, 'Veuillez saisir un code postal valide')
    .required('Champ obligatoire'),
});

function AddressForm({
  address,
  associations,
  dialog,
  disabled,
  onCancel = () => {},
  onStatusChange = () => {},
  onSubmit = () => {},
}) {
  const classes = useStyles();
  const theme = useTheme();

  const [enabled, setEnabled] = useState(Boolean(address.enabled));
  const [actionLoading, setActionLoading] = useState(false);

  const { errors, handleChange, handleSubmit, isSubmitting, resetForm, setFieldValue, setValues, touched, values } =
    useFormik({
      initialValues: { ...address },
      validationSchema: validationSchema,
      onSubmit: async (values) => {
        await onSubmit(values);
      },
    });

  const {
    previousChoice,
    resetShowAction,
    setActionAddress,
    setActionConflictSelect,
    setActionConflictTransfer,
    setActionSelect,
    shouldShowAction,
    showAction,
  } = useShowActionModal(actionType.DEACTIVATE, 'address', deactivateAddress, associations);

  useEffect(() => {
    resetForm();
    setEnabled(address.enabled);
    setValues(address);
  }, [address, resetForm, setValues]);

  function deactivateAddress() {
    onStatusChange({ enabled: false });
  }

  function handleCancelDeactivation() {
    setEnabled(true);
    resetShowAction();
  }

  const handleDeactivateOrDeleteAction = async (action, force = false) => {
    setActionLoading(true);
    const response = await onStatusChange({ action, enabled: false, force });
    setActionLoading(false);
    return response;
  };

  const handleTransferAction = async (selectedAddress, force = false) => {
    setActionLoading(true);
    const response = await onStatusChange({ selectedAddress, enabled: false, force });
    setActionLoading(false);
    return response;
  };

  const handleConfirmActionOnAddressModal = async ({ forceAction, forceTransfer, selectedAction, selectedAddress }) => {
    // Handle action selection submit
    if (selectedAction) {
      if (selectedAction === 'TRANSFER') {
        return setActionAddress();
      }

      // Selected action is DEACTIVATE or DELETE
      const { conflict, success } = await handleDeactivateOrDeleteAction(selectedAction);
      if (conflict) return setActionConflictSelect(selectedAction);
      if (!success) setEnabled(true);
      return resetShowAction();
    }

    // Handle confirmation on delete or deactivation
    if (!isNil(forceAction)) {
      if (forceAction) {
        const { success } = await handleDeactivateOrDeleteAction(previousChoice, true);
        if (!success) setEnabled(true);
        return resetShowAction();
      }

      return setActionSelect();
    }

    // Handle Address selection submit
    if (selectedAddress) {
      const { conflict, success } = await handleTransferAction(selectedAddress);
      if (conflict) return setActionConflictTransfer(selectedAddress);
      if (!success) setEnabled(true);
      return resetShowAction();
    }

    // Handle confirmation on transfer
    if (!isNil(forceTransfer)) {
      if (forceTransfer) {
        const { success } = await handleTransferAction(previousChoice, true);
        if (!success) setEnabled(true);
        return resetShowAction();
      }

      return setActionAddress();
    }

    // Handle preference passed
    setEnabled(false);
    if (associations.length > 0) return setActionSelect();
    resetShowAction();
    const { success } = await onStatusChange({ enabled: false });
    if (!success) setEnabled(true);
  };

  const handleEnabledChange = (checked) => {
    if (checked) {
      setEnabled(true);
      if (!isNil(address.id)) onStatusChange({ enabled: true });
      return;
    }

    if (isNil(address.id)) {
      return setEnabled(false);
    }

    shouldShowAction();
  };

  const handleAddressNameChange = (event, value) => {
    setFieldValue('address_name', value);
  };

  const handleCityChange = (event, value) => {
    setFieldValue('city_name', value);
  };

  const onCancelFormClick = () => {
    if (isSubmitting) return;

    onCancel();
    resetForm();
  };

  return (
    <form autoComplete="off" className={classes.root} onSubmit={handleSubmit}>
      <Grid container justifyContent="center" style={{ height: '100%' }}>
        <Grid
          container
          item
          xs={12}
          md={9}
          alignItems="center"
          justifyContent="center"
          direction="column"
          spacing={3}
          style={{ flexGrow: 1 }}
        >
          {!isNil(address.id) && (
            <Grid container>
              <Grid container item xs={12}>
                <FormControlLabel
                  labelPlacement="start"
                  style={{ margin: '0 8px 16px 0' }}
                  control={
                    <DefaultSwitch
                      checked={enabled}
                      checkedIcon={CheckIcon}
                      color={theme.palette.primary.main}
                      name="enabled"
                      onChange={(_, checked) => handleEnabledChange(checked)}
                    />
                  }
                  label="Statut"
                />
                <SwitchStatus status={enabled} />

                {!enabled && (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      borderRadius: 5,
                      borderStyle: 'thin',
                      borderWidth: 1,
                      height: 40,
                      marginLeft: 10,
                    }}
                  >
                    <FiberManualRecordIcon fontSize="small" style={{ color: theme.palette.tertiary.main }} />
                    <Typography>Offline</Typography>
                  </div>
                )}
              </Grid>
            </Grid>
          )}

          <AddressFields
            disabled={disabled}
            errors={errors}
            handleAddressNameChange={handleAddressNameChange}
            handleChange={handleChange}
            handleCityChange={handleCityChange}
            showAddressTitle
            touched={touched}
            values={values}
          />
        </Grid>

        {!!dialog ? (
          <Grid container item xs={12} justifyContent="center" style={{ marginTop: '40px' }}>
            <DefaultButton cancel onClick={onCancelFormClick}>
              Annuler
            </DefaultButton>
            <DefaultButton disabled={isSubmitting} standard type="submit">
              {isSubmitting ? <CircularProgress size={20} /> : 'Valider'}
            </DefaultButton>
          </Grid>
        ) : (
          <Grid container item xs={12} justifyContent="center">
            <Grid container item xs={12} alignItems="flex-end" justifyContent="center" style={{ marginBottom: '16px' }}>
              {!disabled && (
                <>
                  <DefaultButton cancel onClick={onCancelFormClick}>
                    Annuler
                  </DefaultButton>
                  <DefaultButton color="secondary" disabled={isSubmitting} variant="contained" type="submit">
                    {isSubmitting ? <CircularProgress size={20} /> : 'Valider'}
                  </DefaultButton>
                </>
              )}
            </Grid>
          </Grid>
        )}
      </Grid>

      <ActionOnAddressModal
        address={address}
        loading={actionLoading}
        modalType={actionType.DEACTIVATE}
        onClose={handleCancelDeactivation}
        onConfirm={handleConfirmActionOnAddressModal}
        showAction={showAction}
      />
    </form>
  );
}

export default AddressForm;
